import { Link } from "@tanstack/react-router"

import type { MissingTalentSummary } from "@/lib/talent-tracking-api"

import { Tag } from "@/components/shared/tag"
import { cn } from "@/lib/utils"

import {
  CATEGORY_LABELS,
  CATEGORY_SHORT_LABELS,
  CATEGORY_VALUES,
  getMemberDisplayName,
} from "./constants"

type MatrixRow = {
  userId: string
  displayName: string | null
  missingCount: number
  completedCount: number
}

export function CompactMatrixView({
  rows,
  talents,
}: {
  rows: MatrixRow[]
  talents: MissingTalentSummary[]
}) {
  const categoryGroups = CATEGORY_VALUES.map((category) => ({
    category,
    talents: talents.filter((talent) => talent.category === category),
  })).filter((group) => group.talents.length > 0)

  if (talents.length === 0) {
    return (
      <div className="rounded-[1.2rem] border border-dashed border-border/70 px-4 py-8 text-center text-sm text-muted-foreground">
        No talents match the current filters.
      </div>
    )
  }

  const talentCodeById = new Map(
    talents.map((talent, index) => [talent.id, `T${index + 1}`]),
  )

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Tag>{rows.length} members</Tag>
        <Tag>{talents.length} tracked talents</Tag>
        {categoryGroups.map((group) => (
          <span
            key={group.category}
            className={cn(
              "rounded-full border px-2.5 py-1 font-semibold",
              group.category === "offensive" &&
                "border-warning/30 bg-warning/10 text-warning",
              group.category === "defensive" &&
                "border-success/30 bg-success/10 text-success",
              group.category === "strategic" &&
                "border-info/30 bg-info/10 text-info",
            )}
          >
            {CATEGORY_LABELS[group.category]}: {group.talents.length}
          </span>
        ))}
      </div>

      <div className="grid gap-3 lg:hidden">
        {rows.length > 0 ? (
          rows.map((member) => {
            const memberLabel = getMemberDisplayName(member)

            return (
              <div
                key={member.userId}
                className="rounded-[1.05rem] border border-border/60 bg-card/50 p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <Link
                    to="/talent-members/$userId"
                    params={{ userId: member.userId }}
                    className="text-sm font-semibold text-foreground transition hover:text-primary"
                  >
                    {memberLabel}
                  </Link>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        "inline-flex min-w-8 items-center justify-center rounded-full border px-2 py-1 text-[0.68rem] font-bold",
                        member.missingCount > 0
                          ? "border-warning/30 bg-warning/12 text-warning"
                          : "border-success/35 bg-success/15 text-success",
                      )}
                    >
                      {member.missingCount} missing
                    </span>
                    <Tag className="min-w-8 justify-center bg-background/75 px-2 py-1 text-[0.68rem] text-foreground">
                      {member.completedCount}/{talents.length}
                    </Tag>
                  </div>
                </div>

                <div className="mt-3 grid gap-2">
                  {categoryGroups.map((group) => (
                    <div
                      key={`${member.userId}-${group.category}`}
                      className="rounded-xl border border-border/50 bg-background/45 px-2.5 py-2"
                    >
                      <div
                        className={cn(
                          "text-kicker text-[0.62rem] tracking-[0.16em]",
                          group.category === "offensive" && "text-warning",
                          group.category === "defensive" && "text-success",
                          group.category === "strategic" && "text-info",
                        )}
                      >
                        {CATEGORY_LABELS[group.category]}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {group.talents.map((talent) => {
                          const missing = talent.missingUserIds.includes(
                            member.userId,
                          )
                          return (
                            <span
                              key={`${member.userId}-${talent.id}-mobile`}
                              className={cn(
                                "inline-flex items-center justify-center rounded-full border px-2 py-1 text-[0.64rem] font-bold",
                                missing
                                  ? "border-border/70 bg-card text-muted-foreground"
                                  : "border-success/35 bg-success/18 text-success",
                              )}
                              title={`${memberLabel} is ${missing ? "missing" : "ready"} for ${talent.description}`}
                            >
                              {talentCodeById.get(talent.id)}{" "}
                              {missing ? "-" : "Y"}
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })
        ) : (
          <div className="rounded-[1.2rem] border border-dashed border-border/70 px-4 py-8 text-center text-sm text-muted-foreground">
            No members match the current filters.
          </div>
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-[1.2rem] border border-border/60 bg-background/50 lg:block">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border/60 bg-card/75">
              <th
                rowSpan={2}
                className="text-kicker sticky left-0 z-20 min-w-42 border-r border-border/60 bg-card/85 p-3 text-left text-xs text-muted-foreground"
              >
                Member
              </th>
              <th
                rowSpan={2}
                className="text-kicker z-10 border-r border-border/60 p-3 text-center text-xs text-muted-foreground"
              >
                Missing
              </th>
              {categoryGroups.map((group) => (
                <th
                  key={group.category}
                  colSpan={group.talents.length}
                  className={cn(
                    "border-r border-border/60 p-2  text-center text-kicker text-[0.68rem] last:border-r-0",
                    group.category === "offensive" && "text-warning",
                    group.category === "defensive" && "text-success",
                    group.category === "strategic" && "text-info",
                  )}
                >
                  {CATEGORY_SHORT_LABELS[group.category]}
                </th>
              ))}
              <th
                rowSpan={2}
                className="text-kicker p-3 text-right text-xs text-muted-foreground"
              >
                Done
              </th>
            </tr>
            <tr className="border-b border-border/60 bg-card/65">
              {talents.map((talent, index) => (
                <th
                  key={talent.id}
                  className="text-kicker min-w-14 border-r border-border/40 px-1 py-2 text-center text-[0.65rem] tracking-[0.12em] text-muted-foreground last:border-r-0"
                  title={`${CATEGORY_LABELS[talent.category]} T${talent.tier}: ${talent.description}\nReward: ${talent.reward}`}
                >
                  <div>T{index + 1}</div>
                  <div className="mt-1 text-[0.62rem] font-semibold text-muted-foreground/80">
                    {talent.missingCount}m
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((member) => {
                const memberLabel = getMemberDisplayName(member)

                return (
                  <tr
                    key={member.userId}
                    className="border-b border-border/40 last:border-b-0"
                  >
                    <td className="sticky left-0 z-10 border-r border-border/60 bg-background/96 p-3">
                      <Link
                        to="/talent-members/$userId"
                        params={{ userId: member.userId }}
                        className="text-sm font-medium text-foreground transition hover:text-primary"
                      >
                        {memberLabel}
                      </Link>
                    </td>
                    <td className="border-r border-border/50 p-3 text-center">
                      <span
                        className={cn(
                          "inline-flex min-w-8 items-center justify-center rounded-full border px-2 py-1 text-[0.68rem] font-bold",
                          member.missingCount > 0
                            ? "border-warning/30 bg-warning/12 text-warning"
                            : "border-success/35 bg-success/15 text-success",
                        )}
                      >
                        {member.missingCount}
                      </span>
                    </td>
                    {talents.map((talent) => {
                      const missing = talent.missingUserIds.includes(
                        member.userId,
                      )
                      return (
                        <td
                          key={`${member.userId}-${talent.id}`}
                          className="border-r border-border/30 px-1 py-3 text-center last:border-r-0"
                        >
                          <span
                            className={cn(
                              "inline-flex size-6  items-center justify-center rounded-full border text-[0.64rem] font-bold",
                              missing
                                ? "border-border/70 bg-card text-muted-foreground"
                                : "border-success/35 bg-success/18 text-success",
                            )}
                            title={`${memberLabel} is ${missing ? "missing" : "ready"} for ${talent.description}`}
                          >
                            {missing ? "-" : "Y"}
                          </span>
                        </td>
                      )
                    })}
                    <td className="p-3 text-right text-sm font-semibold text-foreground">
                      {member.completedCount}/{talents.length}
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td
                  colSpan={talents.length + 3}
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                >
                  No members match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-muted-foreground">
        On desktop, hover a talent column for the full description.
      </div>
    </div>
  )
}
